/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
  Entypo,
  FontAwesome,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';

const { width, height } = Dimensions.get('window');

const Scholarships = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const [darkMode, setDarkMode] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [bookmarkedScholarships, setBookmarkedScholarships] = useState([]);
  const [appliedScholarships, setAppliedScholarships] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(null);
  
  // Modal states
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  
  // Animation values
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  
  const [scholarships, setScholarships] = useState([]);
  const [filteredScholarships, setFilteredScholarships] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    open: 0,
    needBased: 0,
    meritBased: 0,
  });

  const filters = [
    { id: 'all', label: 'All Scholarships', icon: 'view-grid' },
    { id: 'open', label: 'Currently Open', icon: 'lock-open' },
    { id: 'upcoming', label: 'Upcoming', icon: 'calendar-clock' },
    { id: 'merit', label: 'Merit-Based', icon: 'trophy' },
    { id: 'need', label: 'Need-Based', icon: 'heart' },
    { id: 'full', label: 'Full Scholarship', icon: 'cash' },
    { id: 'partial', label: 'Partial Scholarship', icon: 'cash-100' },
    { id: 'uk', label: 'UK Only', icon: 'flag' },
    { id: 'international', label: 'International', icon: 'earth' },
    { id: 'women', label: 'Women in STEM', icon: 'gender-female' },
  ];

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    teal: ['#319795', '#81e6d9'],
    pink: ['#d53f8c', '#fbb6ce'],
    indigo: ['#4c51bf', '#6366f1'],
  } : {
    primary: ['#6366f1', '#8b5cf6'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    teal: ['#6366f1', '#8b5cf6'],
    pink: ['#ff9a9e', '#fecfef'],
    indigo: ['#6366f1', '#8b5cf6'],
  };

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
    startAnimations();
    
    return () => {
      const unsubscribe = NetInfo.addEventListener(state => {});
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (route.params?.appliedFilters) {
      setAppliedFilters(route.params.appliedFilters);
      const filtered = applyAdvancedFilters(route.params.appliedFilters);
      setFilteredScholarships(filtered);
    } else {
      filterScholarships();
    }
  }, [searchQuery, selectedFilter, scholarships, route.params?.appliedFilters]);

  useFocusEffect(
    React.useCallback(() => {
      refreshData();
      // Clear applied filters when coming back to main screen
      if (route.params?.appliedFilters) {
        route.params.appliedFilters = null;
      }
    }, [])
  );

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadScholarships(),
        loadBookmarks(),
        loadApplied(),
        loadDarkModePreference(),
      ]);
      calculateStats();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load scholarships. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await loadScholarships();
      calculateStats();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadScholarships = async () => {
    try {
      // Mock scholarships data - In a real app, this would come from an API
      const mockScholarships = [
        {
          id: 'S001',
          title: 'Chevening Scholarships',
          provider: 'UK Government',
          amount: '£50,000',
          coverage: 'Full Scholarship',
          type: 'merit',
          deadline: '2024-11-07',
          status: 'open',
          location: 'United Kingdom',
          eligibility: 'International students',
          category: 'full',
          description: 'The UK government\'s global scholarship programme, funded by the Foreign, Commonwealth and Development Office (FCDO) and partner organisations.',
          benefits: [
            'University tuition fees',
            'A monthly stipend',
            'Travel costs to and from the UK',
            'An arrival allowance',
            'A homeward departure allowance',
            'The cost of one visa application',
            'A travel grant to attend Chevening events in the UK'
          ],
          requirements: [
            'Must be a citizen of a Chevening-eligible country',
            'Return to your country of citizenship for a minimum of two years after your award has ended',
            'Have an undergraduate degree',
            'Have at least two years of work experience',
            'Apply to three different eligible UK university courses'
          ],
          applicationLink: 'https://www.chevening.org/apply/',
          currency: 'GBP',
          duration: '1 year',
          academicLevel: 'Master\'s',
          fields: ['All engineering fields'],
          awardType: 'Government',
          competitionLevel: 'Very High',
          color: '#002147',
          icon: 'crown',
          notificationSent: false,
          amountNumeric: 50000,
        },
        {
          id: 'S002',
          title: 'Rhodes Scholarships',
          provider: 'University of Oxford',
          amount: '£65,000',
          coverage: 'Full Scholarship',
          type: 'merit',
          deadline: '2024-10-01',
          status: 'upcoming',
          location: 'University of Oxford, UK',
          eligibility: 'International students',
          category: 'full',
          description: 'The Rhodes Scholarship is the oldest (first awarded in 1902) and perhaps most prestigious international scholarship programme.',
          benefits: [
            'All university and college fees',
            'University application fee',
            'A living stipend (£17,310 per annum 2023-24)',
            'One economy class airfare to Oxford at the start of the scholarship',
            'One economy flight back to the student\'s home country at the conclusion of the scholarship'
          ],
          requirements: [
            'Academic excellence',
            'Energy to use your talents to the full',
            'Truth, courage, devotion to duty',
            'Sympathy for and protection of the weak',
            'Kindliness, unselfishness and fellowship',
            'Moral force of character and instincts to lead'
          ],
          applicationLink: 'https://www.rhodeshouse.ox.ac.uk/scholarships/',
          currency: 'GBP',
          duration: '2-3 years',
          academicLevel: 'Master\'s, PhD',
          fields: ['All fields including Engineering'],
          awardType: 'University',
          competitionLevel: 'Extremely High',
          color: '#C8102E',
          icon: 'school',
          notificationSent: false,
          amountNumeric: 65000,
        },
        {
          id: 'S003',
          title: 'Commonwealth Scholarships',
          provider: 'Commonwealth Scholarship Commission',
          amount: '£40,000',
          coverage: 'Full Scholarship',
          type: 'merit',
          deadline: '2024-12-18',
          status: 'open',
          location: 'UK Universities',
          eligibility: 'Commonwealth citizens',
          category: 'full',
          description: 'Commonwealth Scholarships for Master\'s and PhD study in the UK are offered for citizens of developing Commonwealth countries.',
          benefits: [
            'Approved airfare from your home country to the UK and return at the end of your award',
            'Approved tuition and examination fees',
            'Stipend (living allowance) at the rate of £1,347 per month, or £1,652 per month for those at universities in the London metropolitan area',
            'Warm clothing allowance, where applicable',
            'Study travel grant towards the cost of study-related travel within the UK or overseas',
            'Provision towards the cost of fieldwork undertaken overseas',
            'Paid mid-term visit (airfare) to your home country'
          ],
          requirements: [
            'Be a citizen of or have been granted refugee status by an eligible Commonwealth country',
            'Be permanently resident in an eligible Commonwealth country',
            'Hold a first degree of at least upper second class (2:1) honours standard',
            'Be unable to afford to study in the UK without this scholarship'
          ],
          applicationLink: 'https://cscuk.fcdo.gov.uk/apply/',
          currency: 'GBP',
          duration: '1-3 years',
          academicLevel: 'Master\'s, PhD',
          fields: ['Engineering, Science, Technology'],
          awardType: 'Government',
          competitionLevel: 'High',
          color: '#012169',
          icon: 'earth',
          notificationSent: false,
          amountNumeric: 40000,
        },
        {
          id: 'S004',
          title: 'GREAT Scholarships',
          provider: 'British Council',
          amount: '£10,000',
          coverage: 'Partial Scholarship',
          type: 'merit',
          deadline: '2024-05-30',
          status: 'open',
          location: 'Various UK Universities',
          eligibility: 'Students from selected countries',
          category: 'partial',
          description: 'GREAT Scholarships are jointly funded by the UK government\'s GREAT Britain Campaign and the British Council with participating UK higher education institutions.',
          benefits: [
            'Minimum £10,000 towards tuition fees',
            'Access to exclusive networking events',
            'Academic and professional development opportunities',
            'Cultural experiences in the UK'
          ],
          requirements: [
            'Be a citizen of an eligible country',
            'Have an undergraduate degree',
            'Be motivated and academically able',
            'Meet the English language requirement',
            'Establish an engagement with the UK as a scholar'
          ],
          applicationLink: 'https://www.britishcouncil.org/study-work-abroad/in-uk/scholarship/great-scholarships',
          currency: 'GBP',
          duration: '1 year',
          academicLevel: 'Master\'s',
          fields: ['Engineering, Technology, Business'],
          awardType: 'Government',
          competitionLevel: 'Medium',
          color: '#E4002B',
          icon: 'trophy',
          notificationSent: false,
          amountNumeric: 10000,
        },
        {
          id: 'S005',
          title: 'Imperial College President\'s PhD Scholarships',
          provider: 'Imperial College London',
          amount: '£21,400',
          coverage: 'Full Scholarship',
          type: 'merit',
          deadline: '2024-11-04',
          status: 'open',
          location: 'Imperial College London',
          eligibility: 'All nationalities',
          category: 'full',
          description: 'The President\'s PhD Scholarships offer outstanding students the opportunity to undertake a research degree at Imperial College London.',
          benefits: [
            'Full funding for tuition fees',
            'A stipend of £21,400 per annum to assist with living costs',
            'A consumables fund of £2,000 per annum',
            'A programme of bespoke opportunities and events'
          ],
          requirements: [
            'Outstanding academic record',
            'High-quality research proposal',
            'Meet Imperial\'s PhD entry requirements',
            'Must be among the highest achievers in their undergraduate cohort'
          ],
          applicationLink: 'https://www.imperial.ac.uk/study/pg/fees-and-funding/scholarships/presidents-phd-scholarships/',
          currency: 'GBP',
          duration: '3.5 years',
          academicLevel: 'PhD',
          fields: ['Engineering, Natural Sciences, Medicine'],
          awardType: 'University',
          competitionLevel: 'Very High',
          color: '#00549F',
          icon: 'microscope',
          notificationSent: false,
          amountNumeric: 21400,
        },
        {
          id: 'S006',
          title: 'University of Cambridge Gates Scholarship',
          provider: 'Gates Cambridge Trust',
          amount: '£55,000',
          coverage: 'Full Scholarship',
          type: 'merit',
          deadline: '2024-10-11',
          status: 'upcoming',
          location: 'University of Cambridge',
          eligibility: 'International students',
          category: 'full',
          description: 'Gates Cambridge Scholarships are highly competitive full-cost scholarships awarded to outstanding applicants from countries outside the UK.',
          benefits: [
            'The full cost of studying at Cambridge',
            'A maintenance allowance (£20,000 for 12 months)',
            'Airfare',
            'Visa costs',
            'Health surcharge',
            'Academic development funding',
            'Family allowance'
          ],
          requirements: [
            'Outstanding intellectual ability',
            'Leadership potential',
            'A commitment to improving the lives of others',
            'A good fit between the applicant\'s qualifications and aspirations and the postgraduate programme at Cambridge'
          ],
          applicationLink: 'https://www.gatescambridge.org/apply/',
          currency: 'GBP',
          duration: '1-4 years',
          academicLevel: 'Master\'s, PhD',
          fields: ['All fields including Engineering'],
          awardType: 'Trust',
          competitionLevel: 'Extremely High',
          color: '#A8C700',
          icon: 'gate',
          notificationSent: false,
          amountNumeric: 55000,
        },
        {
          id: 'S007',
          title: 'Women in Engineering Scholarship',
          provider: 'University of Manchester',
          amount: '£5,000',
          coverage: 'Partial Scholarship',
          type: 'merit',
          deadline: '2024-06-30',
          status: 'open',
          location: 'University of Manchester',
          eligibility: 'Female students',
          category: 'partial',
          description: 'Scholarships for outstanding female students pursuing engineering degrees at the University of Manchester.',
          benefits: [
            '£5,000 per year towards tuition fees',
            'Mentoring from senior female engineers',
            'Networking opportunities',
            'Career development workshops'
          ],
          requirements: [
            'Identify as female',
            'Hold an offer to study an engineering course at Manchester',
            'Demonstrate academic excellence',
            'Show leadership potential',
            'Commit to promoting women in engineering'
          ],
          applicationLink: 'https://www.manchester.ac.uk/study/engineering/scholarships/',
          currency: 'GBP',
          duration: '3-4 years',
          academicLevel: 'Undergraduate',
          fields: ['All Engineering disciplines'],
          awardType: 'University',
          competitionLevel: 'Medium',
          color: '#E31B23',
          icon: 'gender-female',
          notificationSent: false,
          amountNumeric: 5000,
        },
        {
          id: 'S008',
          title: 'Engineering for Development Scholarship',
          provider: 'University of Edinburgh',
          amount: '£15,000',
          coverage: 'Partial Scholarship',
          type: 'need',
          deadline: '2024-07-15',
          status: 'open',
          location: 'University of Edinburgh',
          eligibility: 'Students from developing countries',
          category: 'partial',
          description: 'Scholarships for students from developing countries pursuing engineering degrees with a focus on sustainable development.',
          benefits: [
            '£15,000 towards tuition fees',
            'Research funding',
            'Fieldwork support',
            'Conference attendance grants'
          ],
          requirements: [
            'Citizen of a developing country',
            'Admitted to an engineering programme',
            'Demonstrate financial need',
            'Commitment to development work',
            'Academic excellence'
          ],
          applicationLink: 'https://www.ed.ac.uk/engineering/scholarships',
          currency: 'GBP',
          duration: '1-4 years',
          academicLevel: 'Master\'s, PhD',
          fields: ['Civil, Environmental, Mechanical Engineering'],
          awardType: 'University',
          competitionLevel: 'High',
          color: '#003366',
          icon: 'hammer-wrench',
          notificationSent: false,
          amountNumeric: 15000,
        },
      ];
      
      setScholarships(mockScholarships);
      setFilteredScholarships(mockScholarships);
    } catch (error) {
      console.error('Error loading scholarships:', error);
      throw error;
    }
  };

  const applyAdvancedFilters = (filters) => {
    if (!filters) return scholarships;

    const { selectedFilters, amountRange, deadlineRange } = filters;
    
    return scholarships.filter(scholarship => {
      // Status filter
      if (selectedFilters.status.length > 0 && !selectedFilters.status.includes(scholarship.status)) {
        return false;
      }
      
      // Type filter
      if (selectedFilters.type.length > 0) {
        if (selectedFilters.type.includes('both')) {
          // Accept both merit and need
          if (scholarship.type !== 'merit' && scholarship.type !== 'need') return false;
        } else if (!selectedFilters.type.includes(scholarship.type)) {
          return false;
        }
      }
      
      // Category filter
      if (selectedFilters.category.length > 0) {
        const categoryMatch = selectedFilters.category.some(category => {
          if (category === 'full') return scholarship.category === 'full';
          if (category === 'partial') return scholarship.category === 'partial';
          if (category === 'tuition') return scholarship.coverage.includes('tuition');
          if (category === 'stipend') return scholarship.benefits?.some(b => b.toLowerCase().includes('stipend'));
          return false;
        });
        if (!categoryMatch) return false;
      }
      
      // Location filter
      if (selectedFilters.location.length > 0) {
        const locationMatch = selectedFilters.location.some(loc => {
          if (loc === 'uk') return scholarship.location.includes('UK');
          if (loc === 'international') return scholarship.eligibility.includes('International');
          if (loc === 'england') return scholarship.location.includes('England') || scholarship.location.includes('Oxford') || scholarship.location.includes('Cambridge') || scholarship.location.includes('London');
          if (loc === 'scotland') return scholarship.location.includes('Scotland') || scholarship.location.includes('Edinburgh');
          if (loc === 'wales') return scholarship.location.includes('Wales');
          if (loc === 'northern_ireland') return scholarship.location.includes('Northern Ireland');
          return false;
        });
        if (!locationMatch) return false;
      }
      
      // Academic level filter
      if (selectedFilters.academicLevel.length > 0) {
        const level = String(scholarship.academicLevel ?? '').toLowerCase();
        const levelMatch = selectedFilters.academicLevel.some(academicLevel => {
          if (academicLevel === 'undergraduate') return level.includes('undergraduate');
          if (academicLevel === 'masters') return level.includes('master');
          if (academicLevel === 'phd') return level.includes('phd');
          if (academicLevel === 'postdoc') return level.includes('postdoc');
          if (academicLevel === 'diploma') return level.includes('diploma') || level.includes('certificate');
          return false;
        });
        if (!levelMatch) return false;
      }
      
      // Competition level filter
      if (selectedFilters.competitionLevel.length > 0) {
        const competitionMatch = selectedFilters.competitionLevel.some(level => {
          if (level === 'low') return scholarship.competitionLevel === 'Low';
          if (level === 'medium') return scholarship.competitionLevel === 'Medium';
          if (level === 'high') return scholarship.competitionLevel === 'High';
          if (level === 'very_high') return scholarship.competitionLevel === 'Very High';
          if (level === 'extremely_high') return scholarship.competitionLevel === 'Extremely High';
          return false;
        });
        if (!competitionMatch) return false;
      }
      
      // Amount filter
      if (scholarship.amountNumeric) {
        if (scholarship.amountNumeric < amountRange.min || scholarship.amountNumeric > amountRange.max) {
          return false;
        }
      }
      
      // Days until deadline filter
      const daysRemaining = getDaysRemaining(scholarship.deadline);
      if (daysRemaining < deadlineRange.min || daysRemaining > deadlineRange.max) {
        return false;
      }
      
      return true;
    });
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedScholarships');
      if (bookmarks) {
        setBookmarkedScholarships(JSON.parse(bookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadApplied = async () => {
    try {
      const applied = await AsyncStorage.getItem('appliedScholarships');
      if (applied) {
        setAppliedScholarships(JSON.parse(applied));
      }
    } catch (error) {
      console.error('Error loading applied scholarships:', error);
    }
  };

  const calculateStats = () => {
    const total = scholarships.length;
    const upcoming = scholarships.filter(s => s.status === 'upcoming').length;
    const open = scholarships.filter(s => s.status === 'open').length;
    const needBased = scholarships.filter(s => s.type === 'need').length;
    const meritBased = scholarships.filter(s => s.type === 'merit').length;
    
    setStats({ total, upcoming, open, needBased, meritBased });
  };

  const filterScholarships = () => {
    let filtered = scholarships;
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(scholarship =>
        String(scholarship.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(scholarship.provider ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(scholarship.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        scholarship.fields.some(field => field.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Filter by selected filter
    if (selectedFilter !== 'all') {
      switch (selectedFilter) {
        case 'open':
          filtered = filtered.filter(s => s.status === 'open');
          break;
        case 'upcoming':
          filtered = filtered.filter(s => s.status === 'upcoming');
          break;
        case 'merit':
          filtered = filtered.filter(s => s.type === 'merit');
          break;
        case 'need':
          filtered = filtered.filter(s => s.type === 'need');
          break;
        case 'full':
          filtered = filtered.filter(s => s.category === 'full');
          break;
        case 'partial':
          filtered = filtered.filter(s => s.category === 'partial');
          break;
        case 'uk':
          filtered = filtered.filter(s => s.location.includes('UK'));
          break;
        case 'international':
          filtered = filtered.filter(s => s.eligibility.includes('International'));
          break;
        case 'women':
          filtered = filtered.filter(s => String(s.title ?? '').toLowerCase().includes('women'));
          break;
      }
    }
    
    setFilteredScholarships(filtered);
  };

  const toggleBookmark = async (scholarshipId) => {
    try {
      let newBookmarks;
      if (bookmarkedScholarships.includes(scholarshipId)) {
        newBookmarks = bookmarkedScholarships.filter(id => id !== scholarshipId);
      } else {
        newBookmarks = [...bookmarkedScholarships, scholarshipId];
      }
      
      setBookmarkedScholarships(newBookmarks);
      await AsyncStorage.setItem('bookmarkedScholarships', JSON.stringify(newBookmarks));
      
      // Show feedback
      const isBookmarked = newBookmarks.includes(scholarshipId);
      Alert.alert(
        isBookmarked ? 'Bookmarked!' : 'Bookmark Removed',
        isBookmarked 
          ? 'Scholarship added to your bookmarks' 
          : 'Scholarship removed from bookmarks'
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const markAsApplied = async (scholarshipId) => {
    try {
      let newApplied;
      if (appliedScholarships.includes(scholarshipId)) {
        newApplied = appliedScholarships.filter(id => id !== scholarshipId);
      } else {
        newApplied = [...appliedScholarships, scholarshipId];
      }
      
      setAppliedScholarships(newApplied);
      await AsyncStorage.setItem('appliedScholarships', JSON.stringify(newApplied));
      
      const isApplied = newApplied.includes(scholarshipId);
      Alert.alert(
        isApplied ? 'Marked as Applied!' : 'Application Status Removed',
        isApplied 
          ? 'Scholarship marked as applied. Good luck!' 
          : 'Application status removed'
      );
    } catch (error) {
      console.error('Error marking as applied:', error);
    }
  };

  const shareScholarship = async (scholarship) => {
    try {
      await Share.share({
        title: scholarship.title,
        message: `Check out this scholarship opportunity: ${scholarship.title}\n\nAmount: ${scholarship.amount}\nDeadline: ${scholarship.deadline}\n\n${scholarship.description}\n\nApply here: ${scholarship.applicationLink}`,
      });
    } catch (error) {
      console.error('Error sharing scholarship:', error);
    }
  };

  const openApplicationLink = async (scholarship) => {
    try {
      if (!isOnline) {
        Alert.alert('Offline', 'You need to be online to access the application link.');
        return;
      }
      
      await Linking.openURL(scholarship.applicationLink);
    } catch (error) {
      console.error('Error opening application link:', error);
      Alert.alert('Error', 'Failed to open application link.');
    }
  };

  const showScholarshipDetails = (scholarship) => {
    setSelectedScholarship(scholarship);
    setDetailsModalVisible(true);
  };

  const scheduleReminder = async (scholarship) => {
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Required', 'Please enable notifications to set reminders.');
        return;
      }

      const deadlineDate = new Date(scholarship.deadline);
      const reminderDate = new Date(deadlineDate.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days before

      await Notifications.scheduleNotificationAsync({
        content: {
          title: `⏰ Scholarship Deadline Reminder`,
          body: `${scholarship.title} application closes in 7 days!`,
          data: { scholarshipId: scholarship.id, type: 'scholarship-reminder' },
          sound: 'default',
        },
        trigger: {
          type: 'date',
          date: reminderDate,
        },
      });

      Alert.alert('Reminder Set!', `You'll be notified 7 days before the deadline.`);
    } catch (error) {
      console.error('Error scheduling reminder:', error);
      Alert.alert('Error', 'Failed to set reminder.');
    }
  };

  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#10b981';
      case 'upcoming': return '#f59e0b';
      case 'closed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'merit': return '#3b82f6';
      case 'need': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const renderAppliedFilters = () => {
    if (!appliedFilters) return null;
    
    const filterCount = Object.values(appliedFilters.selectedFilters).flat().length;
    if (filterCount === 0) return null;
    
    return (
      <View style={styles.appliedFiltersContainer}>
        <View style={styles.appliedFiltersHeader}>
          <MaterialCommunityIcons name="filter" size={16} color="#6366f1" />
          <Text style={styles.appliedFiltersText}>Active Filters: {filterCount}</Text>
          <TouchableOpacity 
            style={styles.clearFiltersButton}
            onPress={() => {
              setAppliedFilters(null);
              filterScholarships();
            }}
          >
            <MaterialCommunityIcons name="close" size={14} color="#ef4444" />
            <Text style={styles.clearFiltersText}>Clear</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color={darkMode ? "#6366f1" : "#6366f1"} />
      <Text style={styles.loadingText}>Loading scholarships...</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <MaterialCommunityIcons name="alert-circle-outline" size={64} color="#ef4444" />
      <Text style={styles.errorText}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={loadInitialData}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>🎓 Scholarships</Text>
          <TouchableOpacity 
            style={styles.filterButton}
            onPress={() => navigation.navigate('ScholarshipFilter')}
          >
            <MaterialCommunityIcons name="filter-variant" size={24} color="#ffffff" />
            {appliedFilters && (
              <View style={styles.filterBadge}>
                <Text style={styles.filterBadgeText}>
                  {Object.values(appliedFilters.selectedFilters).flat().length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Discover funding opportunities for your engineering education in the UK
        </Text>
      </LinearGradient>
    </Animated.View>
  );

  const renderStats = () => (
    <Animated.View 
      style={[
        styles.statsContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={styles.statsTitle}>📊 Scholarship Overview</Text>
      
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.success}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="cash-multiple" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.total}</Text>
          <Text style={styles.statLabel}>Total</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.blue}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="lock-open" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.open}</Text>
          <Text style={styles.statLabel}>Open</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.warning}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="calendar-clock" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.upcoming}</Text>
          <Text style={styles.statLabel}>Upcoming</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.purple}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="trophy" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.meritBased}</Text>
          <Text style={styles.statLabel}>Merit-Based</Text>
        </View>
      </View>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <MaterialCommunityIcons name="magnify" size={24} color={darkMode ? "#a0aec0" : "#6b7280"} />
      <TextInput
        style={styles.searchInput}
        placeholder="Search scholarships, providers, fields..."
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderFilterChips = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.filterScroll}
      contentContainerStyle={styles.filterContainer}
    >
      {filters.slice(0, 8).map((filter) => (
        <TouchableOpacity
          key={filter.id}
          style={[
            styles.filterChip,
            selectedFilter === filter.id && styles.filterChipActive,
          ]}
          onPress={() => {
            setSelectedFilter(filter.id);
            setAppliedFilters(null);
          }}
        >
          <MaterialCommunityIcons 
            name={filter.icon} 
            size={16} 
            color={selectedFilter === filter.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.filterText,
            selectedFilter === filter.id && styles.filterTextActive,
          ]}>
            {filter.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderScholarshipCard = (scholarship) => {
    const daysRemaining = getDaysRemaining(scholarship.deadline);
    const isBookmarked = bookmarkedScholarships.includes(scholarship.id);
    const isApplied = appliedScholarships.includes(scholarship.id);
    
    return (
      <TouchableOpacity
        key={scholarship.id}
        style={styles.scholarshipCard}
        onPress={() => showScholarshipDetails(scholarship)}
      >
        <View style={styles.cardHeader}>
          <LinearGradient
            colors={[scholarship.color, `${scholarship.color}80`]}
            style={styles.providerIcon}
          >
            <MaterialCommunityIcons name={scholarship.icon} size={20} color="#ffffff" />
          </LinearGradient>
          
          <View style={styles.cardHeaderInfo}>
            <Text style={styles.scholarshipTitle} numberOfLines={2}>{scholarship.title}</Text>
            <Text style={styles.scholarshipProvider}>{scholarship.provider}</Text>
          </View>
          
          <TouchableOpacity 
            onPress={() => toggleBookmark(scholarship.id)}
            style={styles.bookmarkButton}
          >
            <MaterialCommunityIcons 
              name={isBookmarked ? "bookmark" : "bookmark-outline"} 
              size={20} 
              color={isBookmarked ? "#6366f1" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>
        
        <View style={styles.amountContainer}>
          <Text style={styles.amountText}>{scholarship.amount}</Text>
          <View style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(scholarship.status) }
          ]}>
            <Text style={styles.statusText}>{String(scholarship.status ?? '').toUpperCase()}</Text>
          </View>
        </View>
        
        <Text style={styles.scholarshipDescription} numberOfLines={2}>
          {scholarship.description}
        </Text>
        
        <View style={styles.detailsContainer}>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="calendar" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>
              Deadline: {scholarship.deadline} ({daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'})
            </Text>
          </View>
          
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="map-marker" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>{scholarship.location}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="school" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>{scholarship.academicLevel}</Text>
          </View>
        </View>
        
        <View style={styles.tagsContainer}>
          <View style={[
            styles.typeTag,
            { backgroundColor: getTypeColor(scholarship.type) + '20' }
          ]}>
            <Text style={[
              styles.typeTagText,
              { color: getTypeColor(scholarship.type) }
            ]}>
              {scholarship.type === 'merit' ? '🎯 Merit-Based' : '💝 Need-Based'}
            </Text>
          </View>
          
          <View style={[
            styles.coverageTag,
            { backgroundColor: scholarship.category === 'full' ? '#10b98120' : '#f59e0b20' }
          ]}>
            <Text style={[
              styles.coverageTagText,
              { color: scholarship.category === 'full' ? '#10b981' : '#f59e0b' }
            ]}>
              {scholarship.coverage}
            </Text>
          </View>
        </View>
        
        <View style={styles.cardActions}>
          <TouchableOpacity 
            style={[
              styles.actionButton,
              styles.applyButton,
              isApplied && styles.appliedButton
            ]}
            onPress={() => openApplicationLink(scholarship)}
          >
            <MaterialCommunityIcons 
              name={isApplied ? "check-circle" : "send"} 
              size={16} 
              color="#ffffff" 
            />
            <Text style={styles.actionButtonText}>
              {isApplied ? 'Applied' : 'Apply Now'}
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.actionButton, styles.detailsButton]}
            onPress={() => showScholarshipDetails(scholarship)}
          >
            <MaterialCommunityIcons name="information" size={16} color="#6366f1" />
            <Text style={styles.detailsButtonText}>Details</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.actionButton, styles.shareButton]}
            onPress={() => shareScholarship(scholarship)}
          >
            <MaterialCommunityIcons name="share-variant" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderScholarshipDetailsModal = () => (
    <Modal
      visible={detailsModalVisible}
      animationType="slide"
      onRequestClose={() => setDetailsModalVisible(false)}
    >
      {selectedScholarship && (
        <SafeAreaView style={styles.modalContainer}>
          <LinearGradient
            colors={[selectedScholarship.color, `${selectedScholarship.color}80`]}
            style={styles.modalHeader}
          >
            <View style={styles.modalHeaderContent}>
              <TouchableOpacity 
                style={styles.modalCloseButton}
                onPress={() => setDetailsModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
              </TouchableOpacity>
              <Text style={styles.modalTitle}>{selectedScholarship.title}</Text>
              <Text style={styles.modalSubtitle}>{selectedScholarship.provider}</Text>
            </View>
          </LinearGradient>

          <ScrollView style={styles.modalContent}>
            <View style={styles.modalStats}>
              <View style={styles.modalStat}>
                <Text style={styles.modalStatValue}>{selectedScholarship.amount}</Text>
                <Text style={styles.modalStatLabel}>Amount</Text>
              </View>
              <View style={styles.modalStat}>
                <Text style={styles.modalStatValue}>{selectedScholarship.duration}</Text>
                <Text style={styles.modalStatLabel}>Duration</Text>
              </View>
              <View style={styles.modalStat}>
                <Text style={styles.modalStatValue}>{selectedScholarship.deadline}</Text>
                <Text style={styles.modalStatLabel}>Deadline</Text>
              </View>
            </View>

            <Text style={styles.sectionTitle}>Description</Text>
            <Text style={styles.descriptionText}>{selectedScholarship.description}</Text>

            <Text style={styles.sectionTitle}>Benefits</Text>
            {selectedScholarship.benefits.map((benefit, index) => (
              <View key={index} style={styles.bulletItem}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#10b981" />
                <Text style={styles.bulletText}>{benefit}</Text>
              </View>
            ))}

            <Text style={styles.sectionTitle}>Requirements</Text>
            {selectedScholarship.requirements.map((requirement, index) => (
              <View key={index} style={styles.bulletItem}>
                <MaterialCommunityIcons name="clipboard-check" size={16} color="#3b82f6" />
                <Text style={styles.bulletText}>{requirement}</Text>
              </View>
            ))}

            <Text style={styles.sectionTitle}>Eligibility</Text>
            <Text style={styles.detailText}>{selectedScholarship.eligibility}</Text>

            <Text style={styles.sectionTitle}>Fields of Study</Text>
            <View style={styles.fieldsContainer}>
              {selectedScholarship.fields.map((field, index) => (
                <View key={index} style={styles.fieldTag}>
                  <Text style={styles.fieldText}>{field}</Text>
                </View>
              ))}
            </View>

            <Text style={styles.sectionTitle}>Competition Level</Text>
            <View style={styles.competitionContainer}>
              <Text style={styles.competitionText}>{selectedScholarship.competitionLevel}</Text>
              <View style={styles.competitionBar}>
                <View style={[
                  styles.competitionFill,
                  { 
                    width: selectedScholarship.competitionLevel === 'Extremely High' ? '100%' :
                          selectedScholarship.competitionLevel === 'Very High' ? '80%' :
                          selectedScholarship.competitionLevel === 'High' ? '60%' :
                          selectedScholarship.competitionLevel === 'Medium' ? '40%' : '20%',
                    backgroundColor: selectedScholarship.competitionLevel === 'Extremely High' ? '#ef4444' :
                                    selectedScholarship.competitionLevel === 'Very High' ? '#f59e0b' :
                                    selectedScholarship.competitionLevel === 'High' ? '#3b82f6' :
                                    selectedScholarship.competitionLevel === 'Medium' ? '#10b981' : '#6b7280'
                  }
                ]} />
              </View>
            </View>
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity 
              style={styles.modalBookmarkButton}
              onPress={() => {
                toggleBookmark(selectedScholarship.id);
                setDetailsModalVisible(false);
              }}
            >
              <MaterialCommunityIcons 
                name={bookmarkedScholarships.includes(selectedScholarship.id) ? "bookmark" : "bookmark-outline"} 
                size={20} 
                color="#6366f1" 
              />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.reminderButton}
              onPress={() => scheduleReminder(selectedScholarship)}
            >
              <MaterialCommunityIcons name="bell" size={20} color="#ffffff" />
              <Text style={styles.reminderButtonText}>Set Reminder</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.modalApplyButton}
              onPress={() => {
                openApplicationLink(selectedScholarship);
                setDetailsModalVisible(false);
              }}
            >
              <MaterialCommunityIcons name="send" size={20} color="#ffffff" />
              <Text style={styles.modalApplyButtonText}>Apply Now</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="cash-multiple" size={64} color={darkMode ? "#4a5568" : "#d1d5db"} />
      <Text style={styles.emptyStateTitle}>No scholarships found</Text>
      <Text style={styles.emptyStateText}>
        {searchQuery ? 'Try a different search term' : 
         appliedFilters ? 'No scholarships match your current filters' : 
         'Check back later for new opportunities'}
      </Text>
      <TouchableOpacity 
        style={styles.resetButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedFilter('all');
          setAppliedFilters(null);
          filterScholarships();
        }}
      >
        <Text style={styles.resetButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const styles = getStyles(darkMode);

  if (loading) {
    return renderLoading();
  }

  if (error && !isRefreshing) {
    return renderError();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#6366f1" />
      
      {renderHeader()}
      {renderStats()}
      {renderSearchBar()}
      {renderAppliedFilters()}
      {renderFilterChips()}
      
      <FlatList
        data={filteredScholarships}
        renderItem={({ item }) => renderScholarshipCard(item)}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#6366f1']}
            tintColor="#6366f1"
          />
        }
        ListEmptyComponent={renderEmptyState}
        ListFooterComponent={<View style={styles.bottomPadding} />}
      />

      <TouchableOpacity 
        style={styles.fab}
        onPress={() => navigation.navigate('ScholarshipTips')}
      >
        <LinearGradient
          colors={gradients.success}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="lightbulb-on" size={24} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>

      {renderScholarshipDetailsModal()}
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#374151',
    textAlign: 'center',
    marginVertical: 16,
  },
  retryButton: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    marginBottom: 16,
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 30,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    flex: 1,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  filterButton: {
    padding: 8,
    position: 'relative',
  },
  filterBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#ef4444',
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
    lineHeight: 24,
  },
  appliedFiltersContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 12,
    padding: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  appliedFiltersHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  appliedFiltersText: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 8,
    marginRight: 'auto',
  },
  clearFiltersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderRadius: 6,
  },
  clearFiltersText: {
    fontSize: 12,
    color: '#ef4444',
    fontWeight: '600',
    marginLeft: 4,
  },
  statsContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 16,
    padding: 20,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statCard: {
    alignItems: 'center',
    flex: 1,
  },
  statGradient: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 12,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginLeft: 12,
  },
  filterScroll: {
    flexGrow: 0,
    marginBottom: 16,
  },
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 4,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 38,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 14,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  filterChipActive: {
    backgroundColor: '#6366f1',
    borderColor: '#6366f1',
  },
  filterText: {
    marginLeft: 6,
    fontSize: 13,
    color: darkMode ? '#cbd5e0' : '#374151',
    includeFontPadding: false,
  },
  filterTextActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingBottom: 100,
  },
  bottomPadding: {
    height: 100,
  },
  scholarshipCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginBottom: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  providerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardHeaderInfo: {
    flex: 1,
  },
  scholarshipTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  scholarshipProvider: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  bookmarkButton: {
    padding: 4,
  },
  amountContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  amountText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  scholarshipDescription: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 12,
    lineHeight: 20,
  },
  detailsContainer: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  detailText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  typeTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 8,
  },
  typeTagText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  coverageTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  coverageTagText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 1,
    marginRight: 8,
    justifyContent: 'center',
  },
  applyButton: {
    backgroundColor: '#6366f1',
  },
  appliedButton: {
    backgroundColor: '#10b981',
  },
  actionButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  detailsButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#6366f1',
  },
  detailsButtonText: {
    color: '#6366f1',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  shareButton: {
    backgroundColor: 'transparent',
    padding: 8,
    flex: 0,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginTop: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
    marginBottom: 20,
  },
  resetButton: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  resetButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  modalHeader: {
    paddingTop: 20,
    paddingBottom: 30,
  },
  modalHeaderContent: {
    paddingHorizontal: 20,
  },
  modalCloseButton: {
    padding: 8,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  modalStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
  },
  modalStat: {
    alignItems: 'center',
    flex: 1,
  },
  modalStatValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  modalStatLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 12,
    marginTop: 20,
  },
  descriptionText: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    lineHeight: 24,
    marginBottom: 16,
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 8,
    lineHeight: 20,
  },
  fieldsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fieldTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  fieldText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  competitionContainer: {
    marginBottom: 32,
  },
  competitionText: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 8,
  },
  competitionBar: {
    height: 8,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
  },
  competitionFill: {
    height: '100%',
    borderRadius: 4,
  },
  modalActions: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  modalBookmarkButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  reminderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f59e0b',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 12,
    flex: 1,
    justifyContent: 'center',
  },
  reminderButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  modalApplyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#6366f1',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 2,
    justifyContent: 'center',
  },
  modalApplyButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default Scholarships;